// lib
import React from 'react';
import {
  Box,
  Typography,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import Markdown from 'markdown-to-jsx';
import Link from 'next/link';
// styles
import markdownOverrides from '../../utils/MarkdownOverrides';
import {
  SELLER_FEE,
  BUYER_FEE,
  TENANT_FEE,
  LANLORD_FEE,
  FEE_DETAILS
} from '../../utils/Constants/FeesConstants';
import styles from './styles';

export interface LibLandlordFeeDrawerProps {
  richTextHeading?: string;
  isHelpFee?: boolean;
  feeType: 'sell' | 'buyer' | 'tenant' | 'landlord';
  variant?: 'normal' | 'accordion';
}

function LibLandlordFeeDrawer({
  richTextHeading = '',
  feeType
}: LibLandlordFeeDrawerProps) {
  const overrides = {
    ...markdownOverrides,
    h2: {
      component: Typography,
      props: { component: 'h2', sx: styles.firstHeading }
    },
    h3: {
      component: Typography,
      props: { component: 'h3', sx: styles.secondHeading }
    },
    h4: {
      component: Typography,
      props: { component: 'h4', sx: styles.thirdLongHeading }
    },
    p: {
      component: Typography,
      props: { component: 'p', sx: styles.paragraph }
    },
    hr: {
      component: Divider,
      props: { sx: styles.dash }
    },
    a: {
      component: Link,
      props: { style: styles.anchorTag }
    }
  };

  const feeTypeData: FEE_DETAILS =
    feeType === 'sell'
      ? SELLER_FEE
      : feeType === 'tenant'
      ? TENANT_FEE
      : feeType === 'landlord'
      ? LANLORD_FEE
      : BUYER_FEE;

  return (
    <Box>
      <Box>
        {richTextHeading ? (
          <Box sx={styles.headingbox}>
            <Markdown options={{ overrides }}>{richTextHeading}</Markdown>
          </Box>
        ) : null}

        {feeTypeData?.feesDetails ? (
          <Box>
            {feeTypeData.feesDetails.map((itemX) => (
              <>
                <Box>
                  <Box sx={styles.accHeadings}>
                    <Typography
                      variant="bold"
                      sx={
                        itemX.titleText === 'Other fees'
                          ? styles.smallHeading
                          : styles.titleText
                      }
                    >
                      {itemX.titleText}
                    </Typography>
                    {itemX.value ? (
                      <Typography sx={styles.titleText}>
                        {itemX.value}
                      </Typography>
                    ) : null}
                  </Box>
                  {itemX.feesArray.map((itemY) => (
                    <Box key={itemY.text}>
                      {/* if table */}
                      {itemY.type === 'table-data' && (
                        <Box
                          my={4}
                          sx={styles.tableContainerBox}
                          id="table-only"
                        >
                          <TableContainer sx={{ paddingBottom: '1.5rem' }}>
                            <Table
                              sx={styles.tableMain}
                              aria-label="simple table"
                            >
                              <TableHead>
                                <TableRow>
                                  {itemY.tableData.headingRows.map(
                                    (heading) => (
                                      <TableCell
                                        key={heading.heading}
                                        align="left"
                                        sx={styles.tableHeading}
                                      >
                                        {heading.heading}
                                      </TableCell>
                                    )
                                  )}
                                </TableRow>
                              </TableHead>
                              <TableBody sx={styles.tableBorder}>
                                {itemY.tableData.dataRows.map((row, idx) => (
                                  <TableRow
                                    key={`${row.size}_${idx}`}
                                    sx={styles.tableRow}
                                  >
                                    <TableCell
                                      component="th"
                                      scope="row"
                                      align="left"
                                      sx={styles.tableCell}
                                    >
                                      {row.size}
                                    </TableCell>
                                    <TableCell
                                      align="left"
                                      sx={styles.tableCell}
                                    >
                                      {row.cleaning}
                                    </TableCell>
                                    <TableCell
                                      align="left"
                                      sx={styles.tableCell}
                                    >
                                      {row.inventory}
                                    </TableCell>
                                    <TableCell
                                      align="left"
                                      sx={styles.tableCell}
                                    >
                                      {row.checkOut}
                                    </TableCell>
                                    <TableCell
                                      align="left"
                                      sx={styles.tableCell}
                                    >
                                      {row.electricalReport}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                      {/* if simple list items */}
                      {(itemY.type === 'value-text' ||
                        itemY.type === 'text-only' ||
                        itemY.type === 'value-only') && (
                        <Box sx={styles.feeListDetails}>
                          <Typography
                            variant="bold"
                            sx={
                              itemY.text === ''
                                ? styles.textFeeFullWidth
                                : styles.textFee
                            }
                          >
                            {itemY.text[0] === '*' ? (
                              <Box
                                sx={{
                                  width: {
                                    xs: '20rem',
                                    md: '26rem',
                                    lineHeight: '1.25rem'
                                  }
                                }}
                              >
                                <Markdown options={{ overrides }}>
                                  {itemY.text}
                                </Markdown>
                              </Box>
                            ) : itemY.text === '' ? (
                              itemY.valueFee
                            ) : (
                              itemY.text
                            )}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={
                              itemY.text === ''
                                ? styles.valueFeeEmpty
                                : styles.valueFee
                            }
                          >
                            {itemY.text === '' ? null : itemY.valueFee}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
                {itemX.titleText !== 'Other fees' ? (
                  <Divider sx={styles.dash} />
                ) : null}
              </>
            ))}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export const LandlordFeeDrawer = React.memo(LibLandlordFeeDrawer);

export default LandlordFeeDrawer;
